import { Inject, Injectable, ViewContainerRef } from "@angular/core";
import { Graph } from "@maxgraph/core";
import { DwdmDiagramAbstractConverterTransponders } from "../../diagram/converter/dwdm-diagram-abstract-converter-transponders";
import { TranspondersConverterData } from "../../diagram/converter/dwdm-diagram-converter.models";
import { CustomCell, TransponderElement } from "../../diagram/models/diagram.models";
import { PopoverDataService } from "../../diagram/popover/popover-data.service";
import { PopoverVisibilityManagerService } from "../../diagram/popover/popover-visibility-manager.service";
import { DwdmCircuitDiagnosticData, DwdmPortDiagnosticData, DwdmTransceiverDiagnosticData } from "../../dwdm-circuit.model";
import { PortData } from "../../wizard/steps/1-ends/end-step-circuit-dwdm.model";
import { AlarmSeverity, DiagramCardAlarm, DiagramCardData, DiagramCardInformation } from "@nms-angular-toolkit/nms-diagram-card";
import { CELSIUS, DECIBEL, DECIBEL_MILIWAT, VOLT } from "@nms-ng2/app/shared/constants/units";
import { PopoverHelperService } from "../../diagram/popover/popover-helper.service";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";

/**
 * Responsável por converter @see TranspondersConverterData em @see TransponderElement.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmDiagramDiagnosticConverterTranspondersService extends DwdmDiagramAbstractConverterTransponders {

    private readonly translatedDiagramCardField;

    constructor(private readonly buildElementPopoverService: PopoverDataService,
        private readonly popoverVisibilityManagerService: PopoverVisibilityManagerService,
        private readonly popoverHelperService: PopoverHelperService,
        @Inject(ANGULARJS_TRANSLATE) private readonly translate) {
        super();
        this.translatedDiagramCardField = {
            TEMPERATURE: translate.instant("dwdm.circuit.modal.diagnostic.table.temperature"),
            VOLTAGE: translate.instant("dwdm.circuit.modal.diagnostic.table.voltage"),
            TX_POWER: translate.instant("dwdm.circuit.modal.diagnostic.table.txPower"),
            RX_POWER: translate.instant("dwdm.circuit.modal.diagnostic.table.rxPower"),
            OSNR: translate.instant("dwdm.circuit.modal.diagnostic.table.osnr"),
            PREFEC_BER: translate.instant("dwdm.circuit.modal.diagnostic.table.prefec.ber"),
            FERC: translate.instant("dwdm.circuit.modal.diagnostic.table.ferc"),
            Q_VALUE: translate.instant("dwdm.circuit.modal.diagnostic.table.qvalue"),
            Q_FACTOR: translate.instant("dwdm.circuit.modal.diagnostic.table.qfactor")
        };
    }

    public convert(transpondersData: TranspondersConverterData, dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData,
        diagnosticModalViewRef: ViewContainerRef): TransponderElement[] {

        return super.convertData(transpondersData, (element: TransponderElement, portData: PortData) => {
            if (portData) {
                element.popoverData = this.getPopoverData(dwdmCircuitDiagnosticData, portData);
                element.buildPopover = (graph: Graph, cell: CustomCell) => {
                    const target = this.buildElementPopoverService.create(graph, cell, cell.popoverData);

                    this.popoverVisibilityManagerService.show(diagnosticModalViewRef, target)
                }
            }
        });
    }

    protected shouldBuildDefaultTransponders(transpondersData: TranspondersConverterData): boolean {
        return false;
    }

    protected buildDefaultTransponders(transpondersData: TranspondersConverterData): TransponderElement[] {
        throw new Error("Diagnostic has no default transponders.");
    }

    private getPopoverData(dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData, portData: PortData): DiagramCardData {
        const data = dwdmCircuitDiagnosticData.dwdmTransceivers.find(tranceiver => {
            return tranceiver.circuitPortIdentifierData.deviceResourceId === portData.deviceResourceId
                && tranceiver.circuitPortIdentifierData.chassisIdentifier === portData.chassisNo
                && tranceiver.circuitPortIdentifierData.portNo.toString() === portData.portNo
                && tranceiver.circuitPortIdentifierData.slotIdentifier === portData.slotNo;
        });

        const cardData = new DiagramCardData();
        cardData.title = this.buildTitle(data);
        cardData.informationBlocks = this.informationBlocks(data);
        cardData.alarms = this.buildAlarms(data);

        return cardData;
    }

    private buildTitle(data: DwdmPortDiagnosticData): string {
        const { portModelIdentifier, chassisIdentifier, slotIdentifier, portNo } = data.circuitPortIdentifierData;

        return `${portModelIdentifier} ${chassisIdentifier}/${slotIdentifier}/${portNo}`;
    }

    private informationBlocks(data: DwdmPortDiagnosticData): DiagramCardInformation[] {
        const dwdmTransceiverDiagnosticData = data.dwdmTransceiverDiagnosticData || {} as DwdmTransceiverDiagnosticData;
        const temperatura = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.temperature, CELSIUS);
        const voltage = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.voltage, VOLT);
        const txPower = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.txPower, DECIBEL_MILIWAT);
        const rxPower = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.rxPower, DECIBEL_MILIWAT);
        const osnr = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.osnr, DECIBEL);

        const prefecBer = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.prefecBer);
        const ferc = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.ferc);
        const qValue = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.qValue);
        const qFactor = this.popoverHelperService.getTextOrPlaceHolder(dwdmTransceiverDiagnosticData?.qFactor, DECIBEL);

        return [
            {
                [this.translatedDiagramCardField.TEMPERATURE]: temperatura,
                [this.translatedDiagramCardField.VOLTAGE]: voltage,
                [this.translatedDiagramCardField.TX_POWER]: txPower,
                [this.translatedDiagramCardField.RX_POWER]: rxPower,
                [this.translatedDiagramCardField.OSNR]: osnr
            },
            {
                [this.translatedDiagramCardField.PREFEC_BER]: prefecBer,
                [this.translatedDiagramCardField.FERC]: ferc,
                [this.translatedDiagramCardField.Q_VALUE]: qValue,
                [this.translatedDiagramCardField.Q_FACTOR]: qFactor
            }
        ];
    }

    private buildAlarms(data): DiagramCardAlarm[] {
        return data.alarmsDiagnosticData?.map(alarm => {
            const { information, severity } = alarm;
            const cardSeverity = this.popoverHelperService.normalizeCardSeverity(severity) as AlarmSeverity;

            return { severity: cardSeverity, message: information };
        });
    }
}
